<script setup lang="ts">
import pathToRegexp from 'path-to-regexp'
import { computed } from 'vue'
import type { RouteRecordNormalized } from 'vue-router'
import useRouter from '@/hooks/useRouter'

interface Crumb {
  title: string
  /** A section groups pages in the sidebar and has no page of its own, so it never links. */
  to?: string
}

const { route, router } = useRouter()

const crumbs = computed<Crumb[]>(() => {
  const matched: RouteRecordNormalized[] = route.value.matched
  const leaf = matched[matched.length - 1]
  if (!leaf) return []

  const root = router.getRoutes().find((record) => record.meta.root)
  const section = matched.find((record) => record.meta.section)

  // A modal is a sibling route of the list it opens over, not its child, so `matched` can
  // never name the page underneath — every page whose path prefixes this one is above it.
  const pages = [
    ...matched.slice(0, -1).filter((record) => record.meta.title),
    ...router
      .getRoutes()
      .filter(
        (record) =>
          record.meta.title &&
          !record.meta.hidden &&
          leaf.path.startsWith(`${record.path}/`)
      )
      .sort((a, b) => a.path.length - b.path.length),
    ...(leaf.meta.title ? [leaf] : [])
  ]

  return [
    ...(root && root.path !== leaf.path
      ? [{ title: root.meta.title as string, to: root.path }]
      : []),
    ...(section ? [{ title: section.meta.section as string }] : []),
    ...pages.map((record) => ({
      title: record.meta.title as string,
      to: pathToRegexp.compile(record.path)(route.value.params)
    }))
  ].filter(
    (crumb, index, all) =>
      all.findIndex((other) => other.title === crumb.title) === index
  )
})
</script>

<template>
  <el-breadcrumb class="app-breadcrumb" separator="/">
    <el-breadcrumb-item v-for="(crumb, index) in crumbs" :key="crumb.title">
      <router-link v-if="crumb.to && index < crumbs.length - 1" :to="crumb.to">
        {{ crumb.title }}
      </router-link>
      <span v-else class="no-redirect">{{ crumb.title }}</span>
    </el-breadcrumb-item>
  </el-breadcrumb>
</template>

<style lang="scss" scoped>
.el-breadcrumb__inner,
.el-breadcrumb__inner a {
  font-weight: 400 !important;
}

.app-breadcrumb.el-breadcrumb {
  display: inline-block;
  font-size: 14px;
  line-height: 50px;
  margin-left: 8px;

  .no-redirect {
    color: #97a8be;
    cursor: text;
  }
}
</style>
